import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { NOOP } from '../../../utils/const';
import Spinner from '../../Spinner';
import useRunningState from '../Hooks/useRunningState';
import useChildrenWithText from '../Hooks/useChildrenWithText';

const EMP_HIGH = 'HIGH';
const EMP_LOW = 'LOW';
const EMP_MED = 'MEDIUM';

/*
TODO

Da vedere con UX:
  Usiamo ancora il concetto di tooltip?
  Concetto di "Variante Nero" in enfasi media/bassa. Al momento lo gestisco come secondario!

Migrazione da Button V1 a Button V2:

  Proprietà non più utilizzate perchè sono cambiate le direttive UI.
  * big: non esiste più.
  * flat: non esiste più.
  * floating: non esiste più.
  * icon: non esiste più.
  * selected: non esiste più.
  * text: non esiste più.

  Altro:
  * loading: usare running
*/

/**
I pulsanti permettono agli utenti di compiere azioni e fare scelte con un singolo click.
 */
const Button = forwardRef((props, ref) => {
  const {
    emphasis, primary, vertical, small,
    running: runningInit, disabled, onClick: onClickInit,
    children, className, blockClassName,
    tabIndex,
  } = props;

  const { running, func: onClick } = useRunningState(onClickInit, runningInit);
  const childrenWithText = useChildrenWithText(children);

  const classes = cns(
    blockClassName,
    `${blockClassName}--${emphasis.toLowerCase()}`,
    {
      [`${blockClassName}--primary`]: primary,
      [`${blockClassName}--vertical`]: vertical && emphasis === EMP_LOW,
      [`${blockClassName}--small`]: small && emphasis === EMP_LOW,
    },
    className,
  );

  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      disabled={running || disabled}
      onClick={onClick}
      tabIndex={tabIndex}
    >
      {running && <span className={`${blockClassName}__loader`}><Spinner small /></span>}
      {childrenWithText}
    </button>
  );
});

Button.Emphasis = {
  LOW: EMP_LOW,
  MEDIUM: EMP_MED,
  HIGH: EMP_HIGH,
};

Button.propTypes = {
  /** Enfasi del bottone */
  emphasis: PropTypes.oneOf([Button.Emphasis.LOW, Button.Emphasis.MEDIUM, Button.Emphasis.HIGH]),

  /** Presenta i `children` in verticale (Solo per emphasis = LOW). */
  vertical: PropTypes.bool,

  /** Definisce l'azione principale */
  primary: PropTypes.bool,

  /**
Se true, il bottone non genera alcun evento.
  */
  disabled: PropTypes.bool,

  /**
Se true, il bottone viene mostrato in formato ridotto (Solo per emphasis = LOW).
  */
  small: PropTypes.bool,

  /**
Se true, il bottone visualizza uno spinner ad indicare un processo attivo.
Un bottone in running è sempre disabled.
La gestione della proprietà `running` può essere controlled o uncontrolled.

Gestione controlled: la proprietà assume un valore `true`/`false`.
In questo caso lo stato running è totalmente controllato dalla proprietà ed il componenente non applicherà nessuna logica che lo modifichi.

Gestione uncontrolled: la proprietà assume un valore `undefined`.
In questo caso lo stato running è definito dal componente. Vedi proprtietà `onClick`.
  */
  running: PropTypes.bool,

  /**
Callback evento di click del bottone.
Parametri:

- evento: evento generato dal click.

Ritorno:

- null | undefined | Promise:
se running è uncontrolled e la callback ritorna una Promise, il bottone rimane in stato running fino alla sua conclusione.
  */
  onClick: PropTypes.func,

  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,

  /** Classi css relative al Block (vedi BEM). Ad uso INTERNO. */
  blockClassName: PropTypes.string,

  /** Children del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),

  /** Tab index */
  tabIndex: PropTypes.number,
};

Button.defaultProps = {
  emphasis: Button.Emphasis.LOW,
  vertical: false,
  primary: false,
  disabled: false,
  small: false,
  running: undefined,
  onClick: NOOP,
  children: undefined,
  blockClassName: 'v2-button',
  className: undefined,
  tabIndex: undefined,
};

export default Button;
